import axiosInstance from './axiosConfig';

const IMPERSONATION_SESSION_KEY = 'impersonation_session';

const maskToken = (token) => {
    if (!token || typeof token !== 'string') return 'none';
    if (token.length <= 18) return `${token.slice(0, 6)}...`;
    return `${token.slice(0, 12)}...${token.slice(-6)}`;
};

const summarizeSession = (session) => ({
    role: session?.role || '',
    isAdmin: session?.isAdmin ?? '',
    student_status: session?.student_status || '',
    user_id: session?.user_id || '',
    student_id: session?.student_id || '',
    preceptor_id: session?.preceptor_id || '',
    admin_id: session?.admin_id || '',
    access_token_preview: maskToken(session?.access_token || '')
});

const getDefaultAdminRoute = (role, isAdmin) => {
    if (role === 'admin') {
        return '/StandaloneAdminDashboard/manage-users';
    }
    if (role === 'preceptor' && (isAdmin === true || isAdmin === 'true')) {
        return '/AdminDashboard/manage-users';
    }
    return '/AdminDashboard/manage-users';
};

const captureCurrentSession = () => ({
    access_token: localStorage.getItem('access_token') || '',
    role: localStorage.getItem('role') || '',
    isAdmin: localStorage.getItem('isAdmin') || '',
    student_status: localStorage.getItem('student_status') || '',
    user_id: localStorage.getItem('user_id') || '',
    student_id: localStorage.getItem('student_id') || '',
    preceptor_id: localStorage.getItem('preceptor_id') || '',
    admin_id: localStorage.getItem('admin_id') || ''
});

const applySessionToStorage = (session) => {
    localStorage.setItem('access_token', session.access_token || '');

    if (session.role != null) localStorage.setItem('role', String(session.role));
    if (session.isAdmin != null) localStorage.setItem('isAdmin', String(session.isAdmin));
    if (session.student_status != null) localStorage.setItem('student_status', String(session.student_status));

    // Clear and re-apply IDs so stale impersonated IDs are removed.
    localStorage.removeItem('user_id');
    localStorage.removeItem('student_id');
    localStorage.removeItem('preceptor_id');
    localStorage.removeItem('admin_id');

    if (session.user_id) localStorage.setItem('user_id', String(session.user_id));
    if (session.student_id) localStorage.setItem('student_id', String(session.student_id));
    if (session.preceptor_id) localStorage.setItem('preceptor_id', String(session.preceptor_id));
    if (session.admin_id) localStorage.setItem('admin_id', String(session.admin_id));
};

const getImpersonationSession = () => {
    try {
        const raw = localStorage.getItem(IMPERSONATION_SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        console.warn('Failed to parse impersonation session:', error);
        return null;
    }
};

export const isImpersonating = () => {
    return !!getImpersonationSession() || !!localStorage.getItem('admin_token');
};

export const loginAsUser = async (userId, options = {}) => {
    const normalizedUserId = typeof userId === 'string' ? userId.trim() : String(userId || '').trim();
    const normalizedTargetRole = typeof options.targetRole === 'string' ? options.targetRole.trim().toLowerCase() : '';
    if (!normalizedUserId) {
        console.error('🔴 [IMPERSONATION] Aborted: missing target user id', { userId });
        throw new Error('Cannot login as user: missing target user id.');
    }

    const adminToken = localStorage.getItem('access_token');
    const originalSession = captureCurrentSession();
    const returnPath = options.returnPath || window.location.pathname || getDefaultAdminRoute(originalSession.role, originalSession.isAdmin);

    if (import.meta.env.VITE_DEBUG_MODE === 'true') {
        console.group('🟦 [IMPERSONATION] loginAsUser start');
        console.log('Target user id:', normalizedUserId);
        console.log('Target role hint:', normalizedTargetRole || '(none)');
        console.log('Return path:', returnPath);
        console.log('Original session snapshot:', summarizeSession(originalSession));
        console.log('Outgoing admin token preview:', maskToken(adminToken || ''));
        console.groupEnd();
    }

    try {
        const response = await axiosInstance.post(
            `/auth/admin/login_as_user/${encodeURIComponent(normalizedUserId)}`,
            {},  // empty body since we're passing userId in URL
            {
                params: normalizedTargetRole ? { target_role: normalizedTargetRole } : undefined,
                headers: {
                    'Authorization': `Bearer ${adminToken}`
                }
            }
        );

        if (import.meta.env.VITE_DEBUG_MODE === 'true') {
            console.group('🟩 [IMPERSONATION] API success');
            console.log('API role/isAdmin:', {
                role: response?.data?.role,
                isAdmin: response?.data?.isAdmin,
                student_status: response?.data?.status
            });
            console.log('API id payload:', {
                user_id: response?.data?.user_id || '',
                student_id: response?.data?.student_id || '',
                preceptor_id: response?.data?.preceptor_id || '',
                admin_id: response?.data?.admin_id || ''
            });
            console.log('API token preview:', maskToken(response?.data?.access_token || ''));
            console.groupEnd();
        }

        const impersonatedSession = {
            access_token: response.data.access_token || '',
            role: response.data.role || '',
            isAdmin: !!response.data.isAdmin,
            student_status: response.data.status || '',
            user_id: response.data.user_id || '',
            student_id: response.data.student_id || '',
            preceptor_id: response.data.preceptor_id || '',
            admin_id: response.data.admin_id || ''
        };

        const impersonationSession = {
            originalSession,
            impersonatedSession,
            returnPath,
            startedAt: Date.now()
        };
        
        // Keep backward-compatible token key while also storing full session snapshot.
        localStorage.setItem('admin_token', adminToken);
        localStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(impersonationSession));
        applySessionToStorage(impersonatedSession);

        if (import.meta.env.VITE_DEBUG_MODE === 'true') {
            console.group('🟪 [IMPERSONATION] Session swapped');
            console.log('Stored impersonation session returnPath:', impersonationSession.returnPath);
            console.log('Impersonated session snapshot:', summarizeSession(impersonatedSession));
            console.log('Current storage quick check:', {
                role: localStorage.getItem('role'),
                isAdmin: localStorage.getItem('isAdmin'),
                user_id: localStorage.getItem('user_id'),
                student_id: localStorage.getItem('student_id'),
                preceptor_id: localStorage.getItem('preceptor_id'),
                admin_id: localStorage.getItem('admin_id'),
                access_token_preview: maskToken(localStorage.getItem('access_token') || '')
            });
            console.groupEnd();
        }
        
        return {
            success: true,
            role: response.data.role,
            isAdmin: response.data.isAdmin,
            student_status: response.data.status,
            returnPath
        };
    } catch (error) {
        console.error('🔴 [IMPERSONATION] API failure', {
            targetUserId: normalizedUserId,
            returnPath,
            status: error?.response?.status,
            response: error?.response?.data,
            message: error?.message
        });
        throw new Error(error.response?.data?.detail || 'Failed to login as user');
    }
};

export const restoreAdminSession = () => {
    const session = getImpersonationSession();

    if (import.meta.env.VITE_DEBUG_MODE === 'true') {
        console.group('🟨 [IMPERSONATION] restoreAdminSession');
        console.log('Has modern session:', !!session);
        console.log('Has legacy admin_token:', !!localStorage.getItem('admin_token'));
    }

    if (session?.originalSession?.access_token) {
        applySessionToStorage(session.originalSession);
        localStorage.removeItem(IMPERSONATION_SESSION_KEY);
        localStorage.removeItem('admin_token');

        if (import.meta.env.VITE_DEBUG_MODE === 'true') {
            console.log('Restored modern session snapshot:', summarizeSession(session.originalSession));
            console.log('Restore returnPath:', session.returnPath || getDefaultAdminRoute(session.originalSession.role, session.originalSession.isAdmin));
            console.groupEnd();
        }

        return {
            success: true,
            returnPath: session.returnPath || getDefaultAdminRoute(session.originalSession.role, session.originalSession.isAdmin),
            role: session.originalSession.role,
            isAdmin: session.originalSession.isAdmin,
            student_status: session.originalSession.student_status
        };
    }

    // Fallback for older sessions that only stored admin_token.
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
        localStorage.setItem('access_token', adminToken);
        localStorage.removeItem('admin_token');
        localStorage.removeItem(IMPERSONATION_SESSION_KEY);
        if (import.meta.env.VITE_DEBUG_MODE === 'true') {
            console.log('Restored legacy admin_token path');
            console.groupEnd();
        }
        return {
            success: true,
            returnPath: '/AdminDashboard/manage-users'
        };
    }

    if (import.meta.env.VITE_DEBUG_MODE === 'true') {
        console.log('No impersonation session to restore');
        console.groupEnd();
    }
    return { success: false };
};

// Helper function to get current user ID from various storage locations
export const getCurrentUserId = () => {
    // Try different possible storage keys
    const possibleKeys = ['user_id', 'student_id', 'preceptor_id', 'admin_id'];
    
    for (const key of possibleKeys) {
        const id = localStorage.getItem(key);
        if (id) return id;
    }
    
    // Fallback: try to parse from JWT token
    try {
        const token = localStorage.getItem('access_token');
        if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.sub || payload.user_id || payload.id;
        }
    } catch (error) {
        console.warn('Failed to parse JWT token:', error);
    }
    
    return null;
};

// Helper function to get current user role
export const getCurrentUserRole = () => {
    return localStorage.getItem('user_type') || 
           localStorage.getItem('role') || 
           localStorage.getItem('userType') || 
           'student';
};

// Helper function to check if current user has admin access
export const hasAdminAccess = () => {
    const role = getCurrentUserRole();
    const isAdmin = localStorage.getItem('isAdmin') === 'true' || localStorage.getItem('isAdmin') === true;
    
    // Allow access for:
    // 1. Standalone admins (role: 'admin')
    // 2. Preceptor admins (role: 'preceptor' with isAdmin: true)
    return role === 'admin' || (role === 'preceptor' && isAdmin);
};